import { useTranslation } from 'react-i18next';

interface Check {
  checkType: string;
  outcome: string;
  confidence: number;
  reasoning: string;
}

interface ClaimFindingsPanelProps {
  checks: Check[];
}

const OUTCOME_ICON: Record<string, string> = {
  PASS: '✓',
  WARN: '⚠',
  FAIL: '✗',
  INDETERMINATE: '—',
};

const OUTCOME_COLORS: Record<string, string> = {
  PASS: 'text-success',
  WARN: 'text-copper',
  FAIL: 'text-danger',
  INDETERMINATE: 'text-slate-400',
};

const OUTCOME_BG: Record<string, string> = {
  PASS: 'bg-success/10',
  WARN: 'bg-copper-soft',
  FAIL: 'bg-danger/10',
  INDETERMINATE: 'bg-slate-100',
};

const CONFIDENCE_BAR_COLOR: Record<string, string> = {
  PASS: 'bg-success',
  WARN: 'bg-copper',
  FAIL: 'bg-danger',
  INDETERMINATE: 'bg-slate-300',
};

export function ClaimFindingsPanel({ checks }: ClaimFindingsPanelProps) {
  const { t } = useTranslation();

  if (checks.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-slate-400">
        {t('approver.findings.noChecks', { defaultValue: 'No verification checks available.' })}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {checks.map((c, i) => {
        const pct = Math.round(c.confidence * 100);
        const icon = OUTCOME_ICON[c.outcome] ?? '—';
        const color = OUTCOME_COLORS[c.outcome] ?? 'text-slate-400';
        const bg = OUTCOME_BG[c.outcome] ?? 'bg-slate-100';
        const barColor = CONFIDENCE_BAR_COLOR[c.outcome] ?? 'bg-slate-300';

        return (
          <div
            key={i}
            className="flex items-start gap-3 px-3 py-2 border border-hairline rounded bg-bone"
          >
            {/* Outcome icon */}
            <span
              className={[
                'text-xs font-bold w-6 h-6 flex items-center justify-center rounded-sm shrink-0 mt-0.5',
                color,
                bg,
              ].join(' ')}
            >
              {icon}
            </span>

            {/* Check type + reasoning */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-ink">
                {t(`wizard.findings.checkTypes.${c.checkType}`, { defaultValue: c.checkType })}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 leading-snug">{c.reasoning}</p>
              {/* Confidence bar */}
              <div className="mt-1.5 h-1 bg-slate-100 rounded-sm overflow-hidden w-32">
                <div
                  className={['h-full rounded-sm', barColor].join(' ')}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            {/* Confidence % */}
            <span className="text-xs font-mono text-slate-400 shrink-0 mt-0.5">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}
